import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumToFa } from "../Functions/TextTransform";

const isClearAllFilter = (item) =>
  item?.props?.className?.includes?.("filter_container_clear_all");

const computeRowWidth = (chips, gap, actionsWidth) => {
  let rowWidth = 0;

  chips.forEach((chip, index) => {
    rowWidth += chip.width + (index > 0 ? gap : 0);
  });

  if (actionsWidth) {
    rowWidth += (rowWidth > 0 ? gap : 0) + actionsWidth;
  }

  return rowWidth;
};

const FIT_TOLERANCE_PX = 1;

const getElementOuterInlineSize = (el) => {
  if (!el) {
    return 0;
  }
  const style = getComputedStyle(el);
  const marginStart = parseFloat(style.marginInlineStart) || 0;
  const marginEnd = parseFloat(style.marginInlineEnd) || 0;
  return el.getBoundingClientRect().width + marginStart + marginEnd;
};

export default function SelectedFiltersOverflow({
  filters = [],
  showAllFilters,
  pageLanguage,
  className = "",
}) {
  const { t } = useTranslation();
  const overflowRef = useRef(null);
  const measureListRef = useRef(null);
  const overflowMeasureRef = useRef(null);
  const isExpandedRef = useRef(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  isExpandedRef.current = isExpanded;

  const { filterChips, clearAllContent } = useMemo(() => {
    const chips = [];
    let clearAll = null;

    filters.forEach((item) => {
      if (isClearAllFilter(item)) {
        clearAll = item?.props?.children ?? null;
      } else {
        chips.push(item);
      }
    });

    return { filterChips: chips, clearAllContent: clearAll };
  }, [filters]);

  const recalculateVisibleCount = useCallback(() => {
    if (!overflowRef.current || !measureListRef.current) {
      return;
    }

    const containerWidth = overflowRef.current.getBoundingClientRect().width;
    if (!containerWidth) {
      return;
    }

    const chipEls = measureListRef.current.querySelectorAll(
      "li.filter_container:not(.filter_container_actions_measure)"
    );
    const actionsMeasureEl = measureListRef.current.querySelector(".filter_container_actions_measure");
    const overflowMeasureEl = measureListRef.current.querySelector(".filter_container_overflow_measure");

    if (!chipEls.length) {
      setVisibleCount(0);
      return;
    }

    const gap = parseFloat(getComputedStyle(measureListRef.current).gap) || 12;
    const chipWidths = Array.from(chipEls).map((el, index) => ({
      index,
      width: getElementOuterInlineSize(el),
    }));
    const totalChips = chipWidths.length;

    const getActionsWidth = (hiddenCount) => {
      if (!actionsMeasureEl) {
        return 0;
      }

      if (overflowMeasureEl) {
        overflowMeasureEl.style.display = hiddenCount > 0 ? "" : "none";
      }

      if (hiddenCount > 0 && overflowMeasureRef.current) {
        overflowMeasureRef.current.textContent = isExpandedRef.current
          ? t("Show Less")
          : `+${NumToFa(hiddenCount, pageLanguage)}`;
      }

      return getElementOuterInlineSize(actionsMeasureEl);
    };

    const canFit = (count) => {
      const hiddenCount = totalChips - count;
      return (
        computeRowWidth(chipWidths.slice(0, count), gap, getActionsWidth(hiddenCount)) <=
        containerWidth + FIT_TOLERANCE_PX
      );
    };

    let nextVisibleCount = 0;

    for (let count = totalChips; count >= 0; count -= 1) {
      if (canFit(count)) {
        nextVisibleCount = count;
        break;
      }
    }

    setVisibleCount(nextVisibleCount);
  }, [clearAllContent, pageLanguage, t]);

  useLayoutEffect(() => {
    recalculateVisibleCount();

    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(recalculateVisibleCount) : null;

    if (overflowRef.current?.parentElement && resizeObserver) {
      resizeObserver.observe(overflowRef.current.parentElement);
    }

    if (overflowRef.current && resizeObserver) {
      resizeObserver.observe(overflowRef.current);
    }

    window.addEventListener("resize", recalculateVisibleCount);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", recalculateVisibleCount);
    };
  }, [recalculateVisibleCount, showAllFilters, filters, isExpanded]);

  useEffect(() => {
    setIsExpanded(false);
  }, [filterChips]);

  useEffect(() => {
    if (visibleCount >= filterChips.length) {
      setIsExpanded(false);
    }
  }, [visibleCount, filterChips.length]);

  const hiddenCount = Math.max(0, filterChips.length - visibleCount);
  const hasOverflow = hiddenCount > 0;
  const firstRowChips = filterChips.slice(0, visibleCount);
  const showActions = hasOverflow || Boolean(clearAllContent);

  const overflowToggleButton = (
    <div className="dropdown filter_container_overflow_toggle">
      <button
        aria-expanded={isExpanded}
        type="button"
        className="dropdown_btn dropdown-toggle btn btn-primary"
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <p className="text_underline2">
          {isExpanded ? t("Show Less") : `+${NumToFa(hiddenCount, pageLanguage)}`}
        </p>
      </button>
    </div>
  );

  const actionsChip = showActions ? (
    <li className="filter_container filter_container_actions">
      <div className="filter_container_actions_inner">
        {hasOverflow && overflowToggleButton}
        {clearAllContent}
      </div>
    </li>
  ) : null;

  return (
    <div
      className={`filters_container flex_grow products_page_selected_filters ${className}`.trim()}
      style={{
        marginLeft: pageLanguage === "en" ? "-1.2rem" : "0",
        marginRight: pageLanguage === "fa" ? "-1.2rem" : "0",
      }}
    >
      <div className={showAllFilters ? "" : "filters_hidden_part"} />
      <div ref={overflowRef} className="products_page_selected_filters_overflow">
        <ul
          ref={measureListRef}
          className="products_page_selected_filters_measure"
          aria-hidden="true"
        >
          {filterChips}
          {filterChips.length > 0 && (
            <li className="filter_container filter_container_actions filter_container_actions_measure">
              <div className="filter_container_actions_inner">
                <div className="dropdown filter_container_overflow_measure">
                  <button type="button" className="dropdown_btn btn btn-primary">
                    <p ref={overflowMeasureRef} className="text_underline">
                      +99
                    </p>
                  </button>
                </div>
                {clearAllContent}
              </div>
            </li>
          )}
        </ul>

        {isExpanded ? (
          <ul className="products_page_selected_filters_row products_page_selected_filters_row_expanded">
            {filterChips}
            {actionsChip}
          </ul>
        ) : (
          <ul className="products_page_selected_filters_row">
            {firstRowChips}
            {actionsChip}
          </ul>
        )}
      </div>
    </div>
  );
}
